import { formatLocaleNumber } from '@utils/utils';
import {
  Col,
  DatePicker,
  Divider,
  Form,
  Row,
  Table,
  Tag,
  Typography,
  notification,
} from 'antd';
import { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import { useState } from 'react';
import ContentContainer from '../../../components/contentContainer/ContentContainer';
import { TaggedSelect, TaggedTextInput } from '../../../components/inputs';
import { TaggedDownloadButton, TaggedViewButton } from '../../../components/actionsButtons';
import useDebounce from '../../../hooks/form';
import { defaultFilterData } from '../../../utils/constants';
import { paginationProps } from '../../../utils/tableHelper';
import { downloadEInvoicePdf } from '../src/eInvoiceApis';
import { useEInvoiceFilterOptions, useDocumentsByInvestor } from '../src/eInvoiceQueries';
import { useForm } from 'antd/es/form/Form';

const { Text } = Typography;

interface EInvoiceListFilterData extends eInvoice.EInvoiceListFilterData {
  issuance_date_from?: string;
  issuance_date_to?: string;
  issuanceDateRange?: [dayjs.Dayjs | null, dayjs.Dayjs | null];
}

interface ExpandedEInvoiceProps {
  investorName: string;
  onDownloadPdf: (id: number, eInvoiceNumber: string) => void;
  isDownloadingPdf: boolean;
}

const ExpandedEInvoice = ({ investorName, onDownloadPdf, isDownloadingPdf }: ExpandedEInvoiceProps) => {
  const [form] = useForm<EInvoiceListFilterData>();
  const [tableFilterData, setTableFilterData] = useState<EInvoiceListFilterData>({
    ...defaultFilterData,
  });
  const [eInvoiceNumberInput, setEInvoiceNumberInput] = useState<string>('');

  const { data: filterOptionsData } = useEInvoiceFilterOptions();
  const { data, isLoading } = useDocumentsByInvestor(investorName, tableFilterData);

  useDebounce(eInvoiceNumberInput, () => {
    setTableFilterData((prev) => {
      if (prev.e_invoice_number === eInvoiceNumberInput) return prev;
      return {
        ...prev,
        currentPage: 1,
        e_invoice_number: eInvoiceNumberInput,
      };
    });
  });

  const onPaginationChange = (currentPage: number, pageSize?: number) => {
    setTableFilterData((prev) => ({ ...prev, currentPage, pageSize }));
  };

  const onPreviewPdf = async (id: number) => {
    try {
      const response = await downloadEInvoicePdf(id);
      const blob = response.data;
      const blobUrl = URL.createObjectURL(new Blob([blob], { type: 'application/pdf' }));
      window.open(blobUrl, '_blank');
    } catch {
      notification.error({
        message: 'Preview e-Invoice PDF',
        description: 'Failed to load PDF. Please try again later.',
      });
    }
  };

  const onFilterChange = (changedValues: any, allValues: EInvoiceListFilterData) => {
    if ('e_invoice_number' in changedValues) {
      setEInvoiceNumberInput(changedValues.e_invoice_number || '');
      return;
    }

    const [dateFrom, dateTo] = allValues.issuanceDateRange || [];

    setTableFilterData((prevData) => {
      const updatedData: EInvoiceListFilterData = {
        ...prevData,
        currentPage: 1,
        ...allValues,
        e_invoice_number: eInvoiceNumberInput,
        issuance_date_from: dateFrom ? dateFrom.format('YYYY-MM-DD') : '',
        issuance_date_to: dateTo ? dateTo.format('YYYY-MM-DD') : '',
      };

      delete updatedData.issuanceDateRange;

      return updatedData;
    });
  };

  const getStatusTagColor = (status: string) => {
    switch (status?.toLowerCase()) {
      case 'valid':
        return 'success';
      case 'invalid':
        return 'error';
      case 'submitted':
      default:
        return 'processing';
    }
  };

  const columns: ColumnsType<eInvoice.EInvoiceDocument> = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
    },
    {
      title: 'Tax Payer',
      dataIndex: 'tax_payer',
      key: 'tax_payer',
    },
    {
      title: 'e-Invoice No.',
      dataIndex: 'e_invoice_number',
      key: 'e_invoice_number',
      render: (text) => <Text copyable={{ text }}>{text}</Text>,
    },
    {
      title: 'Issuance Date',
      dataIndex: 'issuance_date_time',
      key: 'issuance_date_time',
      render: (text) => dayjs(text).format('DD MMM YYYY'),
    },
    {
      title: 'Status',
      dataIndex: 'mi_status',
      key: 'mi_status',
      render: (status) => (
        <Tag color={getStatusTagColor(status)} key={status}>
          {status}
        </Tag>
      ),
    },
    {
      title: 'Payable Amount (RM)',
      dataIndex: 'total_payable_amount',
      key: 'total_payable_amount',
      align: 'right',
      render: (amount) =>
        typeof amount === 'number'
          ? formatLocaleNumber(amount)
          : typeof amount === 'string'
            ? formatLocaleNumber(parseFloat(amount))
            : amount,
    },
    {
      title: 'Action',
      key: 'action',
      align: 'center',
      render: (_, record) => {
        if (record.mi_status === 'Cancelled') {
          return null;
        }

        return (
          <>
            <TaggedViewButton
              tooltiptext="View e-Invoice PDF"
              tag="viewEInvoicePdf"
              loading={isLoading}
              onClick={() => onPreviewPdf(record.id)}
            />
            <TaggedDownloadButton
              tooltiptext="Download e-Invoice PDF"
              tag="downloadEInvoicePdf"
              loading={isDownloadingPdf}
              onClick={() => onDownloadPdf(record.id, record.e_invoice_number)}
            />
          </>
        );
      },
    },
  ];

  return (
    <ContentContainer>
      <Form
        form={form}
        layout="vertical"
        onValuesChange={onFilterChange}
      >
        <Row gutter={24}>
          <Col span={8}>
            <Form.Item name="e_invoice_number" label="Filter by e-Invoice No.">
              <TaggedTextInput
                tag="eInvoiceNo"
                placeholder="Enter e-Invoice No."
              />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item name="tax_payer_filter" label="Filter by Tax Payer">
              <TaggedSelect
                tag="taxPayer"
                placeholder="Select Tax Payer"
                options={filterOptionsData?.tax_payers?.map((tp) => ({
                  label: tp,
                  value: tp,
                })) || []}
                allowClear
              />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item name="status_filter" label="Filter by Status">
              <TaggedSelect
                tag="status"
                placeholder="Select Status"
                options={filterOptionsData?.statuses?.map((st) => ({
                  label: st,
                  value: st,
                })) || []}
                allowClear
              />
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={24}>
          <Col span={8}>
            <Form.Item name="issuanceDateRange" label="Issuance Date Range">
              <DatePicker.RangePicker
                style={{ width: '100%' }}
                format="YYYY-MM-DD"
              />
            </Form.Item>
          </Col>
        </Row>
      </Form>

      <Divider />

      <Table<eInvoice.EInvoiceDocument>
        loading={isLoading}
        dataSource={data?.data || []}
        rowClassName={(_, index) => (index % 2 === 0 ? 'dark-stripe' : '')}
        columns={columns}
        rowKey="id"
        pagination={{
          ...paginationProps(data?.totalFiltered),
          current: tableFilterData.currentPage,
          showSizeChanger: true,
          onChange: onPaginationChange,
        }}
        size="small"
      />
    </ContentContainer>
  );
};

export default ExpandedEInvoice;
